import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { CHIP, CategoryChip, Countdown } from '../describe/ui';
import { useT } from '../i18n/useT';
import { TouchBadge } from '../market/TouchPanel';
import { VolumeCompact } from '../volume/VolumeCells';
import { formatVolumeUsd } from '../volume/format';
import { formatCents, type CatalogItem, type SortKey } from './catalog';
import { MAX_QUESTION_ROWS, questionRows, type CatalogEntry } from './group';
import { panelClick } from './layout';
import type { QuestionCardData } from './QuestionCard';
import TagLabel from './TagLabel';

export interface CompactListProps {
  entries: readonly CatalogEntry[];
  linkSearch: string;
  sort: SortKey;
  selected: number | null;
  onSelect: (outcome: number) => void;
  /** The market panel, placed under the selected row. Null while it sits beside the list. */
  panel: ReactNode;
  /** The list shares its width with the panel: rows drop what is not needed to pick an outcome. */
  dense: boolean;
}

/** Column widths shared by the header and the rows, so they line up like a details view. */
const COL_VOL = 'num hidden w-16 shrink-0 text-end text-xs text-muted sm:block';
const colTime = (dense: boolean) => `hidden w-24 shrink-0 truncate text-end text-xs text-muted lg:w-56 ${dense ? '' : 'md:block'}`;
const COL_PRICE = 'w-24 shrink-0 items-baseline justify-end gap-1.5';
const VENUE = `${CHIP} rounded bg-surface-2 px-1.5 font-semibold text-fg`;

function Price({ item }: { item: CatalogItem }) {
  const priced = item.mid !== undefined;
  return (
    <span className={`flex ${COL_PRICE}`} data-testid="compact-price">
      <span className="min-w-0 truncate text-[11px] text-muted" translate="no">
        {item.sides[0]}
      </span>
      <span className={`num shrink-0 font-semibold ${priced ? 'text-yes' : 'text-muted'}`}>{formatCents(item.mid)}</span>
    </span>
  );
}

interface RowProps {
  item: CatalogItem;
  /** The short label under a question, the full title otherwise. */
  label: string;
  nested: boolean;
  linkSearch: string;
  selected: boolean;
  onSelect: (outcome: number) => void;
  panel: ReactNode;
  dense: boolean;
}

/**
 * One outcome. From `sm`: one line, chips then title, then volume, time and price columns. On a phone: the title and
 * price on the first line, the chips under the title.
 */
function Row({ item, label, nested, linkSearch, selected, onSelect, panel, dense }: RowProps) {
  const t = useT();
  const time = item.resolvesAt && !nested ? <Countdown date={item.resolvesAt} label={item.resolvesLabel} /> : null;
  return (
    <li>
      <Link
        to={`/market/${item.outcome}${linkSearch}`}
        onClick={panelClick(item.outcome, onSelect)}
        aria-label={t('catalog.tile_aria', { id: String(item.outcome), title: item.title })}
        aria-current={selected ? 'true' : undefined}
        data-testid="compact-row"
        data-outcome-link={item.outcome}
        data-selected={selected ? 'true' : undefined}
        className={`flex items-center gap-2 rounded-md border px-2 py-1 text-sm transition-colors ${nested ? 'ms-3' : ''} ${
          selected ? 'border-accent bg-accent/15' : 'border-transparent hover:bg-surface-2'
        }`}
      >
        <span className="flex min-w-0 flex-1 flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-2">
          <span className="min-w-0 truncate sm:order-2 sm:flex-1" data-testid="compact-title" translate="no">
            {label}
            {item.subtitle && !nested && !dense ? <span className="text-xs text-muted"> · {item.subtitle}</span> : null}
          </span>
          <span className="flex min-w-0 items-center gap-1.5 text-xs text-muted empty:hidden sm:order-1 sm:shrink-0">
            {nested ? null : (
              <>
                <CategoryChip category={item.category} />
                {item.tagLabel && !dense ? <TagLabel tag={item.tagLabel} /> : null}
                {item.touch ? <TouchBadge /> : null}
                {item.venue ? (
                  <span className={VENUE} translate="no" data-testid="compact-venue">
                    {item.venue}
                  </span>
                ) : null}
                {dense ? null : <span className={`num hidden lg:inline-flex ${CHIP}`}>#{item.outcome}</span>}
              </>
            )}
            <VolumeCompact volume={item.volume} testId="compact-volume-phone" />
            {time ? <span className="min-w-0 truncate text-[11px] sm:hidden">{time}</span> : null}
          </span>
        </span>
        <span className={COL_VOL} data-testid="compact-volume">
          {item.volume ? formatVolumeUsd(item.volume.usd) : null}
        </span>
        <span className={colTime(dense)}>{time}</span>
        <Price item={item} />
      </Link>
      {selected && panel ? <div className="mt-1">{panel}</div> : null}
    </li>
  );
}

type RowContext = Pick<RowProps, 'linkSearch' | 'onSelect' | 'panel' | 'dense'>;

function Question({ group, sort, selected, ...row }: RowContext & { group: QuestionCardData; sort: SortKey; selected: number | null }) {
  const t = useT();
  const venue = group.rows[0]?.item.venue ?? group.fallback?.item.venue;
  const rows = questionRows(group, sort);
  const [open, setOpen] = useState(false);
  // A selected outcome is never folded away.
  const all = open || rows.findIndex((r) => r.item.outcome === selected) >= MAX_QUESTION_ROWS;
  const shown = all ? rows : rows.slice(0, MAX_QUESTION_ROWS);
  const more = rows.length - shown.length;
  const outcomeRow = (r: { item: CatalogItem; label: string }) => (
    <Row key={r.item.outcome} {...row} item={r.item} label={r.label} nested selected={r.item.outcome === selected} />
  );
  return (
    <li aria-label={t('catalog.question_aria', { title: group.title })}>
      <div
        className="flex items-center gap-2 rounded-md bg-surface-2/40 px-2 py-1 text-sm"
        data-testid="compact-question"
        data-question={group.question}
      >
        <span className="flex min-w-0 flex-1 flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-2">
          <span className="min-w-0 truncate font-medium sm:order-2 sm:flex-1" data-testid="compact-question-title" translate="no">
            {group.title}
          </span>
          <span className="flex min-w-0 items-center gap-1.5 text-xs text-muted sm:order-1 sm:shrink-0">
            <CategoryChip category={group.category} />
            {group.tagLabel && !row.dense ? <TagLabel tag={group.tagLabel} /> : null}
            {venue ? (
              <span className={VENUE} translate="no">
                {venue}
              </span>
            ) : null}
            <span className={`${CHIP} truncate`}>
              {t('catalog.question_outcomes', { n: group.rows.length + (group.fallback ? 1 : 0) })}
              {group.settled ? ` · ${t('catalog.question_settled', { n: group.settled })}` : ''}
            </span>
          </span>
        </span>
        <span className={COL_VOL} data-testid="compact-question-volume">
          {group.volume ? formatVolumeUsd(group.volume.usd) : null}
        </span>
        <span className={colTime(row.dense)}>
          {group.resolvesAt ? <Countdown date={group.resolvesAt} label={group.resolvesLabel} /> : null}
        </span>
        <span className={`hidden sm:flex ${COL_PRICE}`} />
      </div>
      <ul>
        {shown.map(outcomeRow)}
        {group.fallback ? outcomeRow(group.fallback) : null}
      </ul>
      {more > 0 ? (
        <button type="button" className="ms-5 px-2 py-0.5 text-xs text-accent hover:underline" onClick={() => setOpen(true)}>
          {t('catalog.show_all', { n: rows.length })}
        </button>
      ) : null}
    </li>
  );
}

/** The only outcome a question lists right now (one left, or a filter): a summary row would add nothing. */
function lone(group: QuestionCardData): CatalogItem | undefined {
  const all = group.fallback ? [...group.rows, group.fallback] : group.rows;
  return all.length === 1 ? all[0]!.item : undefined;
}

/** Compact Markets layout (#1953): one outcome per row, like a file manager's details view. */
export default function CompactList({ entries, linkSearch, sort, selected, onSelect, panel, dense }: CompactListProps) {
  const t = useT();
  const row = { linkSearch, onSelect, panel, dense };
  return (
    <div className="card p-1" data-testid="compact-list">
      <div
        aria-hidden="true"
        className="hidden items-center gap-2 border-b border-border/60 px-2 py-1 text-[11px] uppercase tracking-wide text-muted sm:flex"
      >
        <span className="min-w-0 flex-1">{t('catalog.col_outcome')}</span>
        <span className="w-16 shrink-0 text-end">{t('volume.col_24h')}</span>
        <span className={colTime(dense)} />
        <span className="w-24 shrink-0" />
      </div>
      <ul aria-label={t('catalog.list')} data-tour="catalog-list" className="divide-y divide-border/40">
        {entries.map((e) =>
          e.kind === 'tile' ? (
            <Row
              key={e.item.outcome}
              {...row}
              item={e.item}
              label={e.item.title}
              nested={false}
              selected={e.item.outcome === selected}
            />
          ) : lone(e.group) ? (
            <Row
              key={lone(e.group)!.outcome}
              {...row}
              item={lone(e.group)!}
              label={lone(e.group)!.title}
              nested={false}
              selected={lone(e.group)!.outcome === selected}
            />
          ) : (
            <Question key={`q${e.group.question}`} {...row} group={e.group} sort={sort} selected={selected} />
          ),
        )}
      </ul>
    </div>
  );
}
